import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { 
  fetchCheckouts, 
  selectCheckoutsState, 
  selectIsUserLoggedIn,
  selectIsAdminLoggedIn
} from '../redux/WithcookieSlice';
import { PriceDisplay } from './common/PriceDisplay';
import './OrderHistoryComponent.css';
import SafeImage from './common/SafeImage';
import ReviewModal from './ReviewModal';

// 아이콘 컴포넌트들 추가
const CalendarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="16" y1="2" x2="16" y2="6"></line>
    <line x1="8" y1="2" x2="8" y2="6"></line>
    <line x1="3" y1="10" x2="21" y2="10"></line>
  </svg>
);

const OrderIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
    <line x1="3" y1="6" x2="21" y2="6"></line>
    <path d="M16 10a4 4 0 0 1-8 0"></path>
  </svg>
);

const ShippingIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="3" width="15" height="13"></rect>
    <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
    <circle cx="5.5" cy="18.5" r="2.5"></circle>
    <circle cx="18.5" cy="18.5" r="2.5"></circle>
  </svg>
);

const PriceTagIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
    <line x1="7" y1="7" x2="7.01" y2="7"></line>
  </svg>
);

const OrderHistoryComponent = ({ id, componentProps = {}, style = {}, isPreview = false }) => {
  const { 
    className = "order-history-wrapper padding-tb-40",
    title = "주문 내역",
    emptyText = "주문 내역이 없습니다."
  } = componentProps || {};
  
  const dispatch = useDispatch();
  const { checkouts, loading, error, pagination } = useSelector(selectCheckoutsState);
  const isUserLoggedIn = useSelector(selectIsUserLoggedIn);
  const isAdminLoggedIn = useSelector(selectIsAdminLoggedIn);
  
  // 페이지 상태 관리
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // 한 페이지당 표시할 항목 수
  
  // 리뷰 모달 상태 관리
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  // 리뷰 모달 열기 함수
  const openReviewModal = (productId, orderId, product) => {
    setSelectedProduct(product);
    setSelectedOrder(orderId);
    setReviewModalOpen(true);
  };
  
  // 리뷰 제출 완료 후 호출될 함수
  const handleReviewSubmitted = (reviewData) => {
    // 필요한 경우 추가 로직 (예: 알림 표시)
  };
  
  // 컴포넌트 마운트 시 주문 내역 데이터 로드
  useEffect(() => {
    
    // isPreview가 false이고 로그인되어 있을 때만 API 호출
    if ((isUserLoggedIn || isAdminLoggedIn) && isPreview === false) {
      loadOrders(currentPage, itemsPerPage);
    }
  }, [dispatch, isUserLoggedIn, isAdminLoggedIn, currentPage, itemsPerPage, isPreview]);
  
  // 체크아웃 데이터 변경 시 모니터링
  useEffect(() => {
    if (checkouts && !loading) {
      console.log('현재 주문 내역 데이터:', {
        주문개수: checkouts.length,
        첫번째주문ID: checkouts[0]?.id,
        마지막주문ID: checkouts[checkouts.length - 1]?.id
      });
    }
  }, [checkouts, loading]);
  
  // pagination 디버깅을 위한 useEffect 추가
  useEffect(() => {
    if (pagination) {
      console.log('페이지네이션 정보:', {
        현재페이지: pagination.currentPage,
        총페이지: pagination.totalPages,
        총아이템수: pagination.totalItems,
        현재표시된아이템수: checkouts?.length || 0,
        페이지당아이템수: itemsPerPage
      });
    }
  }, [pagination, itemsPerPage, checkouts]);
  
  // 주문 내역 로드 함수
  const loadOrders = (page, take) => {
    console.log(`주문 내역 로드: 페이지 ${page}, 개수 ${take}`);
    dispatch(fetchCheckouts({ page, take }));
  };
  
  // 페이지 변경 핸들러
  const handlePageChange = (newPage) => {
    if (newPage !== currentPage) {
      console.log(`페이지 변경: ${currentPage} → ${newPage}`);
      setCurrentPage(newPage);
      
      // 페이지 상단으로 스크롤
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };
  
  // 주문 상태에 따른 텍스트 및 스타일 클래스 반환
  const getStatusInfo = (status) => {
    const statusMap = {
      'REQUESTED': { text: '주문 요청됨', class: 'status-requested', icon: '📋' },
      'PENDING': { text: '결제 대기 중', class: 'status-pending', icon: '⏳' },
      'PAID': { text: '결제 완료', class: 'status-paid', icon: '💰' },
      'SHIPPING': { text: '배송 중', class: 'status-shipping', icon: '🚚' },
      'FINISHED': { text: '배송 완료', class: 'status-completed', icon: '✅' },
      'CANCELLED': { text: '주문 취소됨', class: 'status-cancelled', icon: '❌' },
      'REFUNDED': { text: '환불 완료', class: 'status-refunded', icon: '💸' }
    };
    
    return statusMap[status] || { text: status || '알 수 없음', class: 'status-unknown', icon: '❓' };
  };
  
  // 날짜 포맷 함수
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    if (isNaN(date)) return dateString;
    
    const today = new Date();
    const weekdays = ['일', '월', '화', '수', '목', '금', '토'];
    const dayOfWeek = weekdays[date.getDay()];
    
    const formattedDate = `${date.getMonth() + 1}/${date.getDate()}(${dayOfWeek})`;
    
    if (date.getFullYear() !== today.getFullYear()) {
      return `${date.getFullYear()}.${formattedDate}`;
    }
    
    return formattedDate;
  };
  
  // 페이지네이션 컴포넌트
  const Pagination = () => {
    // 페이지네이션 데이터 확인
    const totalPages = pagination?.totalPages || Math.ceil((pagination?.totalItems || checkouts.length) / itemsPerPage) || 1;
    const totalItems = pagination?.totalItems || checkouts.length;
    
    // 페이지 번호가 유효한지 확인
    const validCurrentPage = Math.min(Math.max(1, currentPage), totalPages);
    
    // 표시할 페이지 번호 계산 (최대 5개)
    const pageNumbers = [];
    const startPage = Math.max(1, validCurrentPage - 2);
    const endPage = Math.min(totalPages, startPage + 4);
    
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    
    // 페이지 수가 1 이하면 페이지네이션 표시 안함
    if (totalPages <= 1) return null;
    
    return (
      <div className="pagination-container">
        <button 
          className="pagination-button prev" 
          onClick={() => handlePageChange(1)}
          disabled={validCurrentPage === 1}
        >
          처음
        </button>
        
        <button 
          className="pagination-button prev" 
          onClick={() => handlePageChange(validCurrentPage - 1)}
          disabled={validCurrentPage === 1}
        >
          이전
        </button>
        
        {pageNumbers.map(number => (
          <button
            key={number}
            className={`pagination-button ${number === validCurrentPage ? 'active' : ''}`}
            onClick={() => handlePageChange(number)}
          >
            {number}
          </button>
        ))}
        
        <button 
          className="pagination-button next" 
          onClick={() => handlePageChange(validCurrentPage + 1)}
          disabled={validCurrentPage === totalPages}
        >
          다음
        </button>
        
        <button 
          className="pagination-button next" 
          onClick={() => handlePageChange(totalPages)}
          disabled={validCurrentPage === totalPages}
        >
          마지막
        </button>
        
       
      </div>
    );
  };

  // 로딩 상태 표시
  if (loading) {
    return (
      <div className={className} id={id} style={style}>
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="order-history-loading">
                <p>주문 내역을 불러오는 중입니다...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 에러 표시
  if (error) {
    return (
      <div className={className} id={id} style={style}>
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="order-history-error">
                <p>주문 내역을 불러오는데 실패했습니다: {typeof error === 'object' ? error.message || JSON.stringify(error) : error}</p>
                <button 
                  className="order-action-button primary-action"
                  onClick={() => loadOrders(currentPage, itemsPerPage)}
                >
                  다시 시도
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 로그인 필요
  if (!isUserLoggedIn && !isAdminLoggedIn) {
    return (
      <div className={className} id={id} style={style}>
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="order-history-login-required">
                <p>주문 내역을 확인하려면 로그인이 필요합니다.</p>
                <Link to="/login" className="order-action-button primary-action">로그인</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 주문 내역이 비어있는 경우
  if (!checkouts || (Array.isArray(checkouts) && checkouts.length === 0)) {
    return (
      <div className={className} id={id} style={style}>
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="order-history-empty">
                <h2>{title}</h2>
                <p>{emptyText}</p>
                <Link to="/shop" className="order-action-button secondary-action">
                  쇼핑 계속하기
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 주문 내역이 있는 경우
  return (
    <div className={className} id={id} style={style}>
      <div className="container">
        <div className="row">
          <div className="col-12">
            <h2 className="order-history-title">{title}</h2>
            <p className="order-count">
              총 {pagination && (pagination.totalItems !== undefined ? pagination.totalItems : checkouts.length)}개의 주문 내역이 있습니다.
              {pagination && pagination.totalPages > 1 && 
                ` (${pagination.currentPage}/${pagination.totalPages} 페이지)`
              }
            </p>
          </div>
        </div>
        
        <div className="order-history-list">
          {checkouts.map((order) => {
            const statusInfo = getStatusInfo(order.status);
            
            return (
              <div className="order-item" key={order.id}>
                <div className="order-header">
                  <span className={`order-status ${statusInfo.class}`}>
                    <span className="status-icon">{statusInfo.icon}</span> {statusInfo.text}
                  </span>
                  <span className="order-date">{statusInfo.text === '배송 완료' ? `${formatDate(order.createdAt)} 도착` : formatDate(order.createdAt)}</span>
                </div>
                <div className="order-content">
                  {/* 왼쪽: 대표 상품 이미지 */}
                  <div className="order-product">
                    {order.carts && order.carts.length > 0 ? (
                      <>
                        <div className="product-image">
                          <SafeImage 
                            src={order.carts[0].product?.config?.img_url}
                            alt={order.carts[0].product?.title || "상품"}
                            placeholderStyle={{ width: '100%', height: '100%', background: '#cccccc' }}
                          />
                        </div>
                        <div className="product-info">
                          <div className="product-title">
                            {order.carts[0].product?.title || '상품명 없음'}
                          </div>
                          <div className="product-price">
                            <PriceDisplay 
                              price={order.carts[0].product?.config?.discounted_price || order.carts[0].product?.config?.default_price} 
                            /> <span className="product-count">{order.carts[0].count}개</span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="empty-products">
                        주문 상품 정보가 없습니다.
                      </div>
                    )}
                  </div>
                  
                  {/* 오른쪽: 버튼 영역 */}
                  <div className="order-buttons">
                    <div className="order-actions">
                      {/* <Link 
                        to={`/order-detail/${order.id}`} 
                        className="order-action-button primary-action"
                      >
                        배송조회
                      </Link>
                      
                      <button className="order-action-button secondary-action">
                        교환, 반품 신청
                      </button>
                      
                      <button className="order-action-button secondary-action">
                        장바구니 담기
                      </button> */}
                      
                      {order.status === "FINISHED" && (
                        <button 
                          className="order-action-button secondary-action"
                          onClick={() => openReviewModal(
                            order.carts[0]?.product?.id, 
                            order.carts[0]?.id,
                            order.carts[0]?.product
                          )}
                        >
                          리뷰 작성
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          
          {/* 페이지네이션 */}
          {pagination && pagination.totalPages > 0 && (
            <Pagination />
          )}
        </div>
      </div>
      
      {/* 리뷰 모달 */}
      <ReviewModal 
        isOpen={reviewModalOpen}
        onClose={() => setReviewModalOpen(false)}
        productId={selectedProduct?.id}
        orderId={selectedOrder}
        onReviewSubmitted={handleReviewSubmitted}
      />
    </div>
  );
};

export default OrderHistoryComponent; 